/**
 * TeamSettings.js
 * 
 * Team-Verwaltung in den World Settings.
 * Ermöglicht das Hinzufügen, Verwalten und Entfernen von Team-Mitgliedern.
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { auth, database, firestore, functions } from '../firebaseConfig';
import { ref, get } from 'firebase/database';
import { 
  doc, 
  getDoc, 
  setDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { CREDIT_PLANS } from '../config/creditPlans';
import { useTeam } from '../contexts/TeamContext';
import './TeamSettings.css';

// Member-Typen für die Auswahl
const MEMBER_TYPES = [
  {
    key: 'standard',
    label: 'Leads Only',
    description: 'Access to LeadSwipe only',
    fullDescription: 'Ideal for team members who focus on prospecting. They can swipe and manage leads via LeadSwipe but have no access to other areas.',
    features: ['leads'],
    allowed: [
      'Swipe & manage leads in LeadSwipe',
      'View lead details & contact info',
      'Mark leads as interested or skip'
    ],
    restricted: [
      'Lead board & pipeline view',
      'Inbox & email features',
      'Offers & deal management',
      'Analytics & reporting',
      'Account & world settings'
    ],
    iconClass: 'fa fa-bullseye',
    color: '#4a90e2'
  },
  {
    key: 'master',
    label: 'Full Access',
    description: 'Access to all features',
    fullDescription: 'For trusted team members who need full operational access. They can manage everything across the platform — except billing and account deletion.',
    features: ['all'],
    allowed: [
      'Full lead management & LeadSwipe',
      'Inbox, email drafts & AI email tools',
      'Offers, deals & pipeline management',
      'Analytics & performance reports',
      'World settings & team configuration',
      'World database & contact management'
    ],
    restricted: [
      'Delete the account',
      'Manage billing & subscription'
    ],
    iconClass: 'fa fa-star',
    color: '#357abd'
  }
];

const EMAIL_PLAN_TO_CREDIT_PLAN = {
  standard: 'starter',
  premium: 'pro',
  unlimited: 'enterprise',
};

function TeamSettings({ userId }) {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberType, setNewMemberType] = useState('standard');
  const [newMemberReadOnly, setNewMemberReadOnly] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [openPermissions, setOpenPermissions] = useState({});
  const [memberLimit, setMemberLimit] = useState(2);
  const [currentPlanLabel, setCurrentPlanLabel] = useState('Starter');

  const currentUserId = userId || auth.currentUser?.uid;
  const currentUserEmail = auth.currentUser?.email;
  // Self-Leave benötigt die eingeloggte User-UID (NICHT `currentUserId`,
  // das ist der Team-Owner, wenn ein Member diese Settings öffnet).
  const myUid = auth.currentUser?.uid;
  // leaveTeam kommt aus dem TeamContext; wir nutzen es hier statt eines
  // eigenen httpsCallable, damit der Context sich um Auto-Switch, State-
  // Refresh und availableTeams-Update kümmert (siehe TeamContext.leaveTeam).
  const { leaveTeam } = useTeam();
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (!currentUserId) return;
    loadTeamMembers();
    loadUserPlan();
  }, [currentUserId]);

  const loadUserPlan = async () => {
    try {
      const snapshot = await get(ref(database, `Users/${currentUserId}/emailPlan`));
      const emailPlan = snapshot.exists() ? snapshot.val() : 'standard';
      const creditPlanKey = EMAIL_PLAN_TO_CREDIT_PLAN[emailPlan] || 'starter';
      const plan = CREDIT_PLANS[creditPlanKey];
      setMemberLimit(plan?.features?.teamMembers || 2);
      setCurrentPlanLabel(plan?.label || 'Starter');
    } catch (err) {
      console.error('Failed to load user plan:', err);
    }
  };

  useEffect(() => {
    if (!showAddModal) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [showAddModal]);

  // Auto-hide messages
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const loadTeamMembers = async () => {
    setIsLoading(true);
    try {
      const teamDocRef = doc(firestore, 'teams', currentUserId);
      const teamDocSnap = await getDoc(teamDocRef);

      if (teamDocSnap.exists()) {
        const teamData = teamDocSnap.data();
        const membersList = Object.entries(teamData.members || {}).map(([id, data]) => ({
          id,
          ...data
        }));
        setMembers(membersList);
      } else {
        // Erstelle Team-Dokument wenn es nicht existiert
        await setDoc(teamDocRef, {
          ownerId: currentUserId,
          ownerEmail: currentUserEmail,
          createdAt: serverTimestamp(),
          members: {
            [currentUserId]: {
              email: currentUserEmail,
              role: 'owner',
              features: ['all'],
              addedAt: serverTimestamp()
            }
          }
        });
        setMembers([{
          id: currentUserId,
          email: currentUserEmail,
          role: 'owner',
          features: ['all']
        }]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Team-Mitglieder:', error);
      setError('Failed to load team members');
    } finally {
      setIsLoading(false);
    }
  };

  const nonOwnerMembers = members.filter(m => m.role !== 'owner');
  const isAtLimit = nonOwnerMembers.length >= memberLimit;

  const handleAddMember = async () => {
    setError('');
    setSuccess('');

    if (isAtLimit) {
      setError(`Your ${currentPlanLabel} plan allows up to ${memberLimit} team members. Upgrade to add more.`);
      return;
    }
    
    if (!newMemberEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newMemberEmail.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    if (members.some(m => m.email?.toLowerCase() === newMemberEmail.toLowerCase())) {
      setError('This user is already a team member');
      return;
    }

    setIsSubmitting(true);

    try {
      // Cloud Function aufrufen (läuft mit Admin-Rechten)
      const addTeamMemberFn = httpsCallable(functions, 'addTeamMember');
      const result = await addTeamMemberFn({
        memberEmail: newMemberEmail.trim(),
        memberType: newMemberType,
        readOnly: newMemberReadOnly,
        teamOwnerId: currentUserId
      });

      if (result.data.success) {
        setSuccess(result.data.message);
        setShowAddModal(false);
        setNewMemberEmail('');
        setNewMemberType('standard');
        setNewMemberReadOnly(false);
        await loadTeamMembers();
      }

    } catch (error) {
      console.error('Fehler beim Hinzufügen des Mitglieds:', error);
      
      // Firebase Function Errors haben eine spezielle Struktur
      if (error.code === 'functions/not-found') {
        setError('No user found with this email. The user must create a BonusClub account first.');
      } else if (error.code === 'functions/already-exists') {
        setError(error.message || 'This user is already a team member or in another team.');
      } else {
        setError(error.message || 'Failed to add team member. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Self-Leave: der eingeloggte User entfernt sich SELBST aus dem Team des
  // Owners, das er gerade betrachtet. Läuft über die leaveTeam-Cloud-Function
  // (via TeamContext), weil Firestore-Rules Members keinen Write auf das
  // Team-Dokument des Owners erlauben. Nach Erfolg re-mountet WorldSettings
  // ohnehin, weil activeWorldId auf den eigenen Account zurückspringt.
  const handleLeaveTeam = async () => {
    if (!currentUserId || currentUserId === myUid) {
      // Owner kann sein eigenes Team nicht verlassen. Sollte durch UI-Guard
      // eh nicht passieren, defensiv trotzdem hier abfangen.
      setError('You are the owner of this team and cannot leave it.');
      return;
    }

    if (!window.confirm(
      `Do you want to leave this team? You will lose access to ${currentUserEmail ? 'this account' : 'all data'} immediately. Only the team owner can invite you back.`
    )) {
      return;
    }

    setIsLeaving(true);
    try {
      const success = await leaveTeam(currentUserId);
      if (!success) {
        setError('Failed to leave team. Please try again.');
      }
      // Bei Erfolg wechselt der Context sofort weg – dieses Component wird
      // unmountet, kein State-Update nötig.
    } catch (err) {
      console.error('[handleLeaveTeam] failed:', err);
      setError(err?.message || 'Failed to leave team.');
    } finally {
      setIsLeaving(false);
    }
  };

  const handleRemoveMember = async (memberId, memberEmail) => {
    if (memberId === myUid) {
      // Self-Remove läuft über handleLeaveTeam (mit Cloud Function), nicht
      // über den Kick-Pfad. Sollte durch den UI-Split eigentlich nie
      // getriggert werden, aber defensiv abfangen.
      setError('Use "Leave Team" to remove yourself.');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove ${memberEmail} from your team?`)) {
      return;
    }

    try {
      // Cloud Function aufrufen (läuft mit Admin-Rechten)
      const removeTeamMemberFn = httpsCallable(functions, 'removeTeamMember');
      const result = await removeTeamMemberFn({ memberId, teamOwnerId: currentUserId });

      if (result.data.success) {
        setSuccess(`${memberEmail} was removed from your team`);
        await loadTeamMembers();
      }

    } catch (error) {
      console.error('Fehler beim Entfernen des Mitglieds:', error);
      setError(error.message || 'Failed to remove team member');
    }
  };

  const handleUpdateMemberType = async (memberId, newType) => {
    try {
      // Cloud Function aufrufen (läuft mit Admin-Rechten)
      const updatePermissionsFn = httpsCallable(functions, 'updateTeamMemberPermissions');
      const result = await updatePermissionsFn({
        memberId,
        memberType: newType,
        teamOwnerId: currentUserId
      });

      if (result.data.success) {
        const typeLabel = newType === 'master' ? 'Full Access' : 'Leads Only';
        setSuccess(`Changed to ${typeLabel} Team Member`);
        setEditingMember(null);
        await loadTeamMembers();
      }

    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
      setError(error.message || 'Failed to update permissions');
    }
  };

  const getMemberTypeInfo = (member) => {
    if (member.role === 'owner') {
      return { label: 'Owner', iconClass: 'fa fa-crown', color: '#2c5aa0' };
    }
    const type = MEMBER_TYPES.find(t => t.key === member.memberType) || MEMBER_TYPES[0];
    return { label: type.label, iconClass: type.iconClass, color: type.color };
  };

  if (isLoading) {
    return (
      <div className="tm-loading">
        <div className="tm-spinner" />
        <p>Loading team members...</p>
      </div>
    );
  }

  return (
    <div className="tm">
      <header className="tm-page-head">
        <div>
          <h3 className="tm-page-title">Team</h3>
          <p className="tm-page-sub">
            Invite colleagues and decide what they can do in this world.
          </p>
        </div>
        <div className="tm-page-head-actions">
          <span className="tm-badge">
            {nonOwnerMembers.length} / {memberLimit}
          </span>
          <button
            type="button"
            className="altBtn altBtn--save"
            onClick={() => !isAtLimit && setShowAddModal(true)}
            disabled={isAtLimit}
            title={isAtLimit ? `${currentPlanLabel} plan limit reached (${memberLimit} members)` : 'Add team member'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="8.5" cy="7" r="4"/>
              <line x1="20" y1="8" x2="20" y2="14"/>
              <line x1="23" y1="11" x2="17" y2="11"/>
            </svg>
            {isAtLimit ? 'Limit reached' : 'Add Member'}
          </button>
        </div>
      </header>

      {error && (
        <div className="tm-message error">
          <i className="fa fa-exclamation-circle" />
          {error}
        </div>
      )}
      {success && (
        <div className="tm-message success">
          <i className="fa fa-check-circle" />
          {success}
        </div>
      )}

      <section className="tm-section">
        <header>
          <span className="tm-section-eyebrow">Members</span>
          <h4 className="tm-section-title">People in this world</h4>
          <p className="tm-section-sub">
            Owners keep full control. Everyone else gets Leads Only or Full Access.
          </p>
        </header>

        <div className="tm-list">
          {members.length === 0 ? (
            <div className="tm-empty">
              <span className="tm-icon tm-empty-icon" aria-hidden>
                <i className="fa fa-user-group" />
              </span>
              <h5>No team members yet</h5>
              <p>Add your first teammate to start collaborating.</p>
            </div>
          ) : (
            members.map((member) => {
              const typeInfo = getMemberTypeInfo(member);
              const isOwner = member.role === 'owner';
              const isEditing = editingMember === member.id;
              // Ist DAS die Zeile des eingeloggten Users? Dann zeigen wir für
              // Nicht-Owner-Members einen "Leave Team"-Button statt Edit/
              // Remove – die beiden würden für self serverseitig eh geblockt.
              const isSelf = member.id === myUid;

              return (
                <div key={member.id} className="tm-entry">
                  <div className="tm-avatar">
                    {member.email?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div className="tm-entry-main">
                    <div className="tm-entry-email">
                      {member.email}
                      {isSelf && <span className="tm-pill" style={{ marginLeft: 8 }}>You</span>}
                    </div>
                    <div className="tm-entry-meta">
                      <span className="tm-pill">
                        <i className={typeInfo.iconClass} /> {typeInfo.label}
                      </span>
                      {member.readOnly && <span className="tm-pill">Read-only</span>}
                    </div>
                  </div>

                  {!isOwner && isSelf && (
                    // Selbst-Zeile eines Members: nur "Leave Team". Editieren
                    // und Kicken der eigenen Rolle ergibt keinen Sinn und
                    // wäre serverseitig ohnehin blockiert.
                    <div className="tm-entry-actions">
                      <button
                        type="button"
                        className="altBtn altBtn--petrol"
                        onClick={handleLeaveTeam}
                        disabled={isLeaving}
                        title="Leave this team"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                          <polyline points="16 17 21 12 16 7" />
                          <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                        {isLeaving ? 'Leaving…' : 'Leave Team'}
                      </button>
                    </div>
                  )}

                  {!isOwner && !isSelf && (
                    <div className="tm-entry-actions">
                      {isEditing ? (
                        <div className="tm-toggle">
                          {MEMBER_TYPES.map((type) => (
                            <button
                              type="button"
                              key={type.key}
                              className={`tm-toggle-btn ${member.memberType === type.key ? 'active' : ''}`}
                              onClick={() => handleUpdateMemberType(member.id, type.key)}
                            >
                              <i className={type.iconClass} /> {type.label}
                            </button>
                          ))}
                          <button
                            type="button"
                            className="tm-icon-btn"
                            onClick={() => setEditingMember(null)}
                          >
                            <i className="fa fa-times" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="tm-icon-btn"
                            onClick={() => setEditingMember(member.id)}
                            title="Change permissions"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="tm-icon-btn"
                            onClick={() => handleRemoveMember(member.id, member.email)}
                            title="Remove member"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3,6 5,6 21,6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>

      <section className="tm-section">
        <header>
          <span className="tm-section-eyebrow">Permissions</span>
          <h4 className="tm-section-title">What each role can do</h4>
          <p className="tm-section-sub">
            Open a role to see what is included and what stays locked.
          </p>
        </header>

        <div className="tm-perm-list">
          {MEMBER_TYPES.map((type) => {
            const isOpen = !!openPermissions[type.key];
            return (
              <div key={type.key} className={`tm-perm ${isOpen ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="tm-perm-head"
                  aria-expanded={isOpen}
                  onClick={() => setOpenPermissions((prev) => ({
                    ...prev,
                    [type.key]: !prev[type.key],
                  }))}
                >
                  <span className="tm-icon" aria-hidden>
                    <i className={type.iconClass} />
                  </span>
                  <span className="tm-perm-copy">
                    <span className="tm-perm-title">{type.label}</span>
                    <span className="tm-perm-desc">{type.description}</span>
                  </span>
                  <i className="fa fa-chevron-down tm-perm-chevron" aria-hidden />
                </button>
                {isOpen && (
                  <div className="tm-perm-body">
                    <p className="tm-card-desc">{type.fullDescription}</p>
                    <ul className="tm-points">
                      {type.allowed.map((item) => (
                        <li key={item}><i className="fa fa-circle-check" />{item}</li>
                      ))}
                      {type.restricted.map((item) => (
                        <li key={item} className="is-off"><i className="fa fa-xmark" />{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {showAddModal && createPortal(
        <div className="tm-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="tm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tm-modal-head">
              <div>
                <span className="tm-modal-eyebrow">Team</span>
                <h3>Add Team Member</h3>
                <p>They need a BonusClub account first. Then pick what they can do here.</p>
              </div>
              <button type="button" className="tm-modal-close" onClick={() => setShowAddModal(false)}>
                <i className="fa fa-times" />
              </button>
            </div>

            <div className="tm-modal-body">
              <div>
                <label className="tm-field-label">Email address</label>
                <input
                  type="email"
                  value={newMemberEmail}
                  onChange={(e) => setNewMemberEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  className="tm-input"
                  autoFocus
                />
                <span className="tm-hint">The user must already have a BonusClub account.</span>
              </div>

              <div>
                <label className="tm-field-label">Permission level</label>
                <div className="tm-type-list">
                  {MEMBER_TYPES.map((type) => (
                    <button
                      type="button"
                      key={type.key}
                      className={`tm-type ${newMemberType === type.key ? 'is-selected' : ''}`}
                      onClick={() => setNewMemberType(type.key)}
                    >
                      <span className="tm-icon" aria-hidden>
                        <i className={type.iconClass} />
                      </span>
                      <span className="tm-type-copy">
                        <span className="tm-type-title">{type.label}</span>
                        <span className="tm-type-desc">{type.description}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <label className="tm-check">
                <input
                  type="checkbox"
                  checked={newMemberReadOnly}
                  onChange={(e) => setNewMemberReadOnly(e.target.checked)}
                />
                <span>
                  <span className="tm-check-title">Read-only access</span>
                  <span className="tm-check-hint">Member can view but not edit data</span>
                </span>
              </label>
            </div>

            <div className="tm-modal-foot">
              <button type="button" className="altBtn altBtn--petrol" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="altBtn altBtn--save"
                onClick={handleAddMember}
                disabled={isSubmitting || !newMemberEmail.trim()}
              >
                {isSubmitting ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default TeamSettings;
